import * as React from "react"

import { createPortal } from "react-dom"

import { Toaster as Sonner, type ToasterProps } from "sonner"

import { usePortalContainer } from "@/components/ui/portal-container"

/**
 * Lifts toasts clear of whatever is fixed to the bottom of the viewport:
 * `MobileBottomNav` below `lg`, the `min-h-16` footer and its links above it.
 *
 * Sonner paints above everything, so a toast at the default offset sits on top
 * of the nav and swallows taps on it.
 */
const CLEAR_FIXED_CHROME = {
  bottom: "calc(88px + env(safe-area-inset-bottom))",
}

const Toaster = (props: ToasterProps) => {
  const portalContainer = usePortalContainer()

  if (!portalContainer) {
    // Both offsets are set on purpose: Sonner switches to `--mobile-offset-*`
    // at 600px, which is inside the range that has the mobile nav.
    return (
      <Sonner
        position="bottom-right"
        offset={CLEAR_FIXED_CHROME}
        mobileOffset={CLEAR_FIXED_CHROME}
        {...props}
      />
    )
  }

  // The embed portals into its shadow root, but the toaster is still
  // `position: fixed` against the host page's viewport, so it keeps the bottom
  // placement instead of floating over a partner's own header.
  return createPortal(
    <Sonner
      className="toaster group !bottom-[50px]"
      position="bottom-right"
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
        } as React.CSSProperties
      }
      {...props}
    />,
    portalContainer,
  )
}

export { Toaster }
